import { JobApplication, JobApplicationAcceptanceStatus, JobApplicationTerminalMessage, StyledText } from '@bluecrew/crew-member-backend-angular-client'

/**
 * Returns the legacy terminal message or the default terminal message if the status is unknown.
 * @returns terminal message
 */ 
export const getLegacyTerminalMessage = (jobApp: JobApplication) => {
    const status = jobApp.acceptanceStatus;

    if (isStatusPagePresent(status)) {
        return {
            buttonText: statusToButton[status] || 'Find another job',
            returnToJobsPage: status !== 'failedBluecrewRequirements',
            showPosition: statusToShowPosition[status],
            bodyText: statusToBodyText[status]?.(jobApp),
            headerIcon: statusToIcon[status]?.(jobApp),
            headerText: statusToHeader[status]?.(jobApp),
        } as JobApplicationTerminalMessage;
    } 

    return {
        buttonText: 'Find another job',
        returnToJobsPage: true,
        showPosition: false,
        bodyText: [
            {
                text: `Please look for another job.`
            } as StyledText
        ],
        headerIcon: '/assets/icons/job-not-available.svg',
        headerText: `We're sorry! We ran into an issue with this job.`,
    } as JobApplicationTerminalMessage;
}

const isStatusPagePresent = (status: JobApplicationAcceptanceStatus) => {
    return [
        JobApplicationAcceptanceStatus.Success,
        JobApplicationAcceptanceStatus.AddedToWaitlist,
        JobApplicationAcceptanceStatus.Overlap,
        JobApplicationAcceptanceStatus.FailedBluecrewRequirements,
        JobApplicationAcceptanceStatus.FailedJobRequirements,
        JobApplicationAcceptanceStatus.PendingWaitlistCheck,
        JobApplicationAcceptanceStatus.NotAvailable,
    ].includes(status);
}

const statusToHeader: { [key in JobApplicationAcceptanceStatus]?: (jobApp: JobApplication) => string } = {
    success: _ => 'You got the job!',
    addedToWaitlist: jobApp => jobApp.isApplicationJob ? 'Additional steps required' : "You're on the wait list!",
    overlap: _ => "We're sorry!",
    notAvailable: _ => "We're sorry! The job has been filled.",
    failedJobRequirements: _ => 'This job isn’t a good fit',
    failedBluecrewRequirements: _ => 'Thank you for your time and interest in Bluecrew',
};

const statusToBodyText: { [key in JobApplicationAcceptanceStatus]?: (jobApp: JobApplication) => StyledText[] } = {
    addedToWaitlist: jobApp => {
        if (jobApp.isApplicationJob) {
            return [{
                text: 'The Bluecrew team will contact you soon to discuss next steps.'
            }];
        }
        return [{
            text: 'The Bluecrew team will contact you if a spot opens up.'
        }];
    },
    notAvailable: jobApp => {
        return [{
            text: 'All openings for ',
        }, {
            text: `${jobApp.positionBrief.positionName} - ${jobApp.positionBrief.workplaceName}`,
            styling: [
                {
                    type: 'font-weight',
                    value: '600'
                }
            ]
        }, {
            text: ' have been filled. Please look for other jobs.'
        }];
    },
    overlap: _ => {
        return [{
            text: 'This job conflicts with another job you are currently assigned to.',
        }];
    },
    failedJobRequirements: _ => {
        return [{
            text: 'Based on your answers, you do not meet the requirements for this job. Please browse available jobs.',
        }];
    },
    failedBluecrewRequirements: _ => {
        return [{
            text: 'We don’t have any opportunities for you right now. However, we encourage you to apply again after 90 days by writing to support@bluecrewjobs.com in order to reset your account. Best of luck in your job search!',
        }];
    },
};

const statusToIcon: { [key in JobApplicationAcceptanceStatus]?: (jobApp: JobApplication) => string } = {
    success: _ => '/assets/icons/accept-job.svg',
    addedToWaitlist: jobApp => jobApp.isApplicationJob ? '/assets/icons/application-job.svg' : '/assets/icons/waitlist-job.svg',
    notAvailable: _ => '/assets/icons/job-not-available.svg',
    overlap: _ => '/assets/icons/job-not-available.svg',
    failedJobRequirements: _ => '/assets/icons/job-not-available.svg',
    failedBluecrewRequirements: _ => '/assets/icons/failed-bluecrew.svg',
};

const statusToShowPosition: { [key in JobApplicationAcceptanceStatus]?: boolean } = {
    success: true,
    addedToWaitlist: true,
    pendingWaitlistCheck: true,
};

const statusToButton: { [key in JobApplicationAcceptanceStatus]?: string } = {
    failedBluecrewRequirements: 'Next',
    success: 'Next',
    addedToWaitlist: 'Next',
    pendingWaitlistCheck: 'Next',
};
